import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useStudents } from "@/contexts";
import { useEnrollmentHistory } from "@/hooks/useEnrollmentHistory";
import { EnrollmentEventsList } from '@/components/student-details/EnrollmentEventsList';
import { Button } from '@/components/ui/button';
import { FileText, Printer, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { printStudentHistoryAsJPG, printStudentHistoryAsPDF } from '@/utils/printing/historyPrinting';
import { useActionTracking } from '@/hooks/useActionTracking';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { paymentTrackingService } from '@/services/payments/paymentTrackingService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { getStudentEnrollmentHistory } from '@/services/db/enrollment/enrollmentHistoryService';
import AdminFormatDialog from '@/components/admin/AdminFormatDialog';

const StudentHistoryTab = () => {
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const { students, loading: loadingStudents, refreshStudents } = useStudents();
  const { events, loading, fetchHistory } = useEnrollmentHistory(Number(selectedStudentId));
  const { getStudentHistory } = useActionTracking();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("enrollment");
  const [showFormatDialog, setShowFormatDialog] = useState(false);

  // Initial data load when component mounts
  useEffect(() => {
    refreshStudents();
  }, []);

  // Fetch history when a student is selected
  useEffect(() => {
    if (selectedStudentId) {
      console.log("Fetching history for student:", selectedStudentId);
      fetchHistory(Number(selectedStudentId));
      fetchPaymentHistory(Number(selectedStudentId));
    }
  }, [selectedStudentId, fetchHistory]);

  const fetchPaymentHistory = async (studentId: number) => {
    try {
      const history = await paymentTrackingService.getPaymentStatusHistory(studentId);
      console.log("Payment history:", history);
      setPaymentHistory(history);
    } catch (error) {
      console.error("Error fetching payment history:", error);
    }
  };

  const handleRefresh = async () => {
    if (!selectedStudentId) {
      toast.error("Please select a student first");
      return;
    }
    
    setIsRefreshing(true);
    try {
      // First refresh student data to ensure we have the latest records
      await refreshStudents();
      
      // Then fetch specific history records
      await fetchHistory(Number(selectedStudentId));
      await fetchPaymentHistory(Number(selectedStudentId));
      
      toast.success("History refreshed");
    } catch (error) {
      console.error("Error refreshing history:", error);
      toast.error("Failed to refresh history");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePrintHistory = async (format: 'jpg' | 'pdf') => {
    if (!selectedStudentId) {
      toast.error("Please select a student first");
      return;
    }
    
    const selectedStudent = students.find(s => s.id.toString() === selectedStudentId);
    if (!selectedStudent) {
      toast.error("Student not found");
      return;
    }

    try {
      console.log(`🖨️ Starting ${format.toUpperCase()} download for student history...`);
      
      // Prepare comprehensive history data including enrollment events and payment history
      const comprehensiveHistory = [
        // Add enrollment events with proper property access
        ...events.map(event => ({
          timestamp: event.timestamp,
          type: event.eventType || 'Enrollment Activity',
          description: event.details || `${event.eventType} event`,
          details: event.details || 'No additional details'
        })),
        // Add payment history
        ...paymentHistory.map(payment => ({
          timestamp: payment.changedAt,
          type: 'Payment Status Change',
          description: `Payment status changed from ${payment.previousStatus} to ${payment.newStatus}`,
          details: `Program: ${payment.programId}, Amount: ${payment.amount ? `AFN ${payment.amount.toFixed(2)}` : 'N/A'}`
        })),
        // Add program enrollment information with safe property access
        ...selectedStudent.programs.map(program => ({
          timestamp: program.enrolledAt,
          type: 'Program Enrollment',
          description: `Enrolled in ${program.title}`,
          details: `Fee Status: ${program.feeStatus}, Program: ${program.title}`
        }))
      ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      if (format === 'jpg') {
        await printStudentHistoryAsJPG(selectedStudent, comprehensiveHistory);
      } else {
        await printStudentHistoryAsPDF(selectedStudent, comprehensiveHistory);
      }
      console.log(`✅ ${format.toUpperCase()} download completed successfully`);
    } catch (error) {
      console.error(`❌ Error downloading student history ${format.toUpperCase()}:`, error);
      toast.error(`Failed to download student history as ${format.toUpperCase()}`);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Student History Records</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Select value={selectedStudentId} onValueChange={setSelectedStudentId}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="Select a student" />
              </SelectTrigger>
              <SelectContent>
                {loadingStudents ? (
                  <SelectItem value="loading" disabled>Loading students...</SelectItem>
                ) : students.length === 0 ? (
                  <SelectItem value="empty" disabled>No students found</SelectItem>
                ) : (
                  students.map((student) => (
                    <SelectItem key={student.id} value={student.id.toString()}>
                      {student.surname ? `${student.name} ${student.surname}` : student.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleRefresh} 
                disabled={!selectedStudentId || isRefreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              {selectedStudentId && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowFormatDialog(true)}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Download History
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading history...</div>
          ) : !selectedStudentId ? (
            <div className="text-center py-4 text-muted-foreground">
              Select a student to view their enrollment history
            </div>
          ) : (
            <div className="space-y-4">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                  <TabsTrigger value="enrollment">Enrollment History</TabsTrigger>
                  <TabsTrigger value="payments">Payment History</TabsTrigger>
                  <TabsTrigger value="status">Program Status</TabsTrigger>
                </TabsList>
                
                <TabsContent value="enrollment">
                  {events.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      No enrollment history records found for this student
                    </div>
                  ) : (
                    <EnrollmentEventsList events={events} />
                  )}
                </TabsContent>
                
                <TabsContent value="payments">
                  {paymentHistory.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      No payment history records found for this student
                    </div>
                  ) : (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Payment History</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Program</TableHead>
                              <TableHead>Status Change</TableHead>
                              <TableHead>Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {paymentHistory.map((payment, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{format(new Date(payment.changedAt), 'MMM d, yyyy')}</TableCell>
                                <TableCell>{payment.programId}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">{payment.previousStatus}</Badge>
                                    <span>→</span>
                                    <Badge 
                                      variant={payment.newStatus === 'paid' ? 'default' : 
                                             payment.newStatus === 'partial' ? 'secondary' : 'destructive'}
                                    >
                                      {payment.newStatus}
                                    </Badge>
                                  </div>
                                </TableCell>
                                <TableCell>{payment.amount ? `AFN ${payment.amount.toFixed(2)}` : 'N/A'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
                
                <TabsContent value="status">
                  {/* Display student's program status */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Current Program Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Program</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Enrollment Date</TableHead>
                            <TableHead>Fee Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {students.find(s => s.id.toString() === selectedStudentId)?.programs.map((program, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{program.title}</TableCell>
                              <TableCell>Active</TableCell>
                              <TableCell>{new Date(program.enrolledAt).toLocaleDateString()}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  program.feeStatus === 'paid' ? 'bg-green-100 text-green-800' : 
                                  program.feeStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' : 
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {program.feeStatus}
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>

        <AdminFormatDialog
          isOpen={showFormatDialog}
          onClose={() => setShowFormatDialog(false)}
          onDownload={handlePrintHistory}
          downloadType="list"
        />
      </CardContent>
    </Card>
  );
};

export default StudentHistoryTab;
